import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Share2, Smartphone, Check } from "lucide-react";
import { toast } from "sonner";

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const SHARE_DATA = {
  title: "Aurea — Depth-led Wellbeing",
  text: "I've been using Aurea, a calming AI wellbeing companion grounded in Carl Jung's insights. Thought you might find it meaningful too.",
  url: window.location.origin,
};

export const InstallShareCard = () => {
  const [canInstall, setCanInstall] = useState(!!window.__deferredInstallPrompt);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onAvail = () => setCanInstall(true);
    const onInstalled = () => { setInstalled(true); setCanInstall(false); };
    window.addEventListener("pwa-installable", onAvail);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onAvail);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    const ev = window.__deferredInstallPrompt;
    if (!ev) {
      toast.info(isIOS()
        ? "On iPhone: tap the Share icon in Safari, then 'Add to Home Screen'."
        : "Open your browser menu and choose 'Install app' or 'Add to Home Screen'.");
      return;
    }
    ev.prompt();
    const { outcome } = await ev.userChoice;
    if (outcome === "accepted") toast.success("Aurea is being added to your home screen.");
    window.__deferredInstallPrompt = null;
    setCanInstall(false);
  };

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share(SHARE_DATA);
      } else {
        await navigator.clipboard.writeText(SHARE_DATA.url);
        toast.success("Link copied — share it with someone who matters.");
      }
    } catch {
      /* user cancelled share — ignore */
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-linen-3 p-6 sm:p-8 space-y-4" data-testid="install-share-card">
      <h2 className="font-serif text-2xl text-ink flex items-center gap-2">
        <Smartphone className="w-5 h-5 text-brand" /> Take Aurea with you
      </h2>
      <p className="text-sm text-ink-2 leading-relaxed">
        Save Aurea to your phone's home screen for a quiet, app-like space — and share this calm corner with someone who could use it.
      </p>
      <div className="flex flex-wrap gap-3">
        {installed ? (
          <span className="inline-flex items-center gap-2 text-sm text-brand bg-calm rounded-full px-4 py-2.5" data-testid="install-done">
            <Check className="w-4 h-4" /> Added to your device
          </span>
        ) : (
          <Button onClick={install} className="rounded-full bg-brand hover:bg-brand-hover text-linen" data-testid="install-button">
            <Download className="w-4 h-4 mr-2" /> Add to Home Screen
          </Button>
        )}
        <Button onClick={share} variant="outline" className="rounded-full border-brand text-brand hover:bg-linen-2" data-testid="share-button">
          <Share2 className="w-4 h-4 mr-2" /> Share Aurea
        </Button>
      </div>
      {isIOS() && !installed && (
        <p className="text-xs text-ink-mute">On iPhone, tap the <span className="font-medium">Share</span> icon in Safari, then <span className="font-medium">Add to Home Screen</span>.</p>
      )}
    </div>
  );
};

export const ShareButton = ({ className = "", testid = "share-quick" }) => {
  const share = async () => {
    try {
      if (navigator.share) await navigator.share(SHARE_DATA);
      else { await navigator.clipboard.writeText(SHARE_DATA.url); toast.success("Link copied to share."); }
    } catch { /* cancelled */ }
  };
  return (
    <button onClick={share} className={className} data-testid={testid}>
      <Share2 className="w-4 h-4" /> Share
    </button>
  );
};
